<template>
  <div class="category-detail-container" :class="categoryThemeClass">
    <Header />

    <main class="category-detail-main">
      <!-- Breadcrumb Bar -->
      <nav aria-label="breadcrumb" class="breadcrumb-section">
        <div class="container px-3 px-md-4">
          <ol class="breadcrumb">
            <li class="breadcrumb-item">
              <router-link to="/">Trang chủ</router-link>
            </li>
            <li class="breadcrumb-item active" aria-current="page">
              {{ category ? category.name : 'Thể loại sách' }}
            </li>
          </ol>
        </div>
      </nav>

      <!-- Editorial Category Hero Banner -->
      <section class="category-hero-section">
        <div class="container px-3 px-md-4">
          <div class="category-hero-card">
            <div class="category-hero-main">
              <div class="category-hero-body">
                <div class="category-icon-avatar">
                  <icon :icon="categoryIcon" />
                </div>
                <div class="category-info-col">
                  <div class="category-label-tag">Thể loại sách</div>
                  <h1 class="category-title">
                    <span v-if="category">{{ category.name }}</span>
                    <span v-else-if="isLoading" class="skeleton-shimmer d-inline-block" style="width: 220px; height: 32px; border-radius: 6px;"></span>
                    <span v-else>Thể loại sách</span>
                  </h1>
                  <p v-if="categoryDescription" class="category-desc">
                    {{ categoryDescription }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Right Stat Column: Clean Editorial Metric Card -->
            <div class="category-hero-aside">
              <div class="author-metric-card">
                <div class="metric-card-header">
                  <span class="metric-card-title">Dữ liệu tại Lối Đọc</span>
                </div>
                <div class="metric-card-body">
                  <div class="metric-item">
                    <span class="metric-value">{{ totalElements }}</span>
                    <span class="metric-label">Tác phẩm</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Books Catalog Section -->
      <section class="category-catalog-section">
        <div class="container px-3 px-md-4">
          <!-- Filter & Controls Toolbar -->
          <div class="catalog-toolbar">
            <div class="toolbar-left">
              <h2 class="catalog-heading">
                Danh sách tác phẩm
              </h2>
            </div>

            <div class="toolbar-right">
              <!-- In-Page Search -->
              <div class="search-filter-box">
                <icon icon="fa-solid fa-magnifying-glass" class="search-icon" />
                <input
                  type="text"
                  v-model="searchQuery"
                  @input="onSearchInput"
                  class="search-filter-input"
                  placeholder="Tìm kiếm sách trong danh mục..."
                />
                <button
                  v-if="searchQuery"
                  class="clear-filter-btn"
                  @click="clearSearch"
                  title="Xóa tìm kiếm"
                >
                  <icon icon="fa-solid fa-xmark" />
                </button>
              </div>

              <!-- Quick Sort Dropdown -->
              <div class="sort-select-wrapper">
                <select v-model="sortBy" @change="onSortChange" class="sort-select">
                  <option value="default">Sắp xếp: Mặc định</option>
                  <option value="rating-desc">Đánh giá cao nhất</option>
                  <option value="name-asc">Tên sách (A - Z)</option>
                  <option value="name-desc">Tên sách (Z - A)</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Loading State (Skeleton Grid) -->
          <div v-if="isLoading" class="row row-cols-2 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-5 g-3 g-md-4">
            <div v-for="n in 10" :key="'skel-' + n" class="col">
              <div class="skeleton-book-card">
                <div class="skeleton-shimmer skeleton-cover"></div>
                <div class="skeleton-shimmer skeleton-line skeleton-title"></div>
                <div class="skeleton-shimmer skeleton-line skeleton-author"></div>
              </div>
            </div>
          </div>

          <!-- Error State -->
          <div v-else-if="errorMessage" class="state-empty-card">
            <div class="state-icon-circle text-danger">
              <icon icon="fa-solid fa-circle-exclamation" />
            </div>
            <h3 class="state-title">Đã xảy ra lỗi tải dữ liệu</h3>
            <p class="state-desc">{{ errorMessage }}</p>
            <button @click="retry" class="btn btn-outline-dark px-4 rounded-pill">
              <icon icon="fa-solid fa-rotate-right" class="me-2" />Thử lại
            </button>
          </div>

          <!-- Empty State (No search results when searching) -->
          <div v-else-if="books.length === 0 && searchQuery" class="state-empty-card">
            <div class="state-icon-circle">
              <icon icon="fa-solid fa-filter-circle-xmark" />
            </div>
            <h3 class="state-title">Không tìm thấy sách phù hợp</h3>
            <p class="state-desc">Không có cuốn sách nào khớp với từ khóa "<strong>{{ searchQuery }}</strong>".</p>
            <button @click="clearSearch" class="btn btn-outline-dark px-4 rounded-pill">
              <icon icon="fa-solid fa-rotate-left" class="me-2" />Xóa tìm kiếm
            </button>
          </div>

          <!-- Empty State (No books at all) -->
          <div v-else-if="books.length === 0" class="state-empty-card">
            <div class="state-icon-circle">
              <icon icon="fa-solid fa-book-open-reader" />
            </div>
            <h3 class="state-title">Chưa có tác phẩm nào</h3>
            <p class="state-desc">Danh mục này hiện chưa có ấn phẩm được cập nhật. Bạn vui lòng quay lại sau hoặc khám phá các chủ đề khác.</p>
            <router-link to="/" class="btn btn-dark px-4 rounded-pill">
              <icon icon="fa-solid fa-arrow-left" class="me-2" />Khám phá trang chủ
            </router-link>
          </div>

          <!-- Book Grid Catalog -->
          <div v-else>
            <div class="row row-cols-2 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-5 g-3 g-md-4">
              <div v-for="book in books" :key="book.id" class="col">
                <BookCard
                  :item="book"
                  mode="catalog"
                  @click="$router.push('/book/' + (book.id || book.bookId))"
                />
              </div>
            </div>

            <!-- Pagination Controls -->
            <nav v-if="totalPages > 1" class="d-flex justify-content-center mt-5" aria-label="Category books navigation">
              <ul class="custom-pagination d-flex align-items-center gap-1 list-unstyled p-1.5 rounded-pill shadow-sm">
                <!-- Previous Button -->
                <li class="page-item-custom">
                  <button
                    @click="goToPage(currentPage - 1)"
                    :disabled="currentPage === 0"
                    class="page-link-custom prev-next-btn d-flex align-items-center justify-content-center"
                    aria-label="Trang trước"
                  >
                    <icon icon="fa-solid fa-chevron-left" />
                  </button>
                </li>

                <!-- Numeric page links -->
                <li
                  v-for="page in visiblePages"
                  :key="page"
                  class="page-item-custom"
                >
                  <!-- Ellipsis placeholder -->
                  <span v-if="page === '...'" class="page-link-ellipsis">...</span>
                  <!-- Page link button -->
                  <button
                    v-else
                    @click="goToPage(page)"
                    :class="['page-link-custom numeric-btn d-flex align-items-center justify-content-center', { 'active': currentPage === page }]"
                  >
                    {{ page + 1 }}
                  </button>
                </li>

                <!-- Next Button -->
                <li class="page-item-custom">
                  <button
                    @click="goToPage(currentPage + 1)"
                    :disabled="currentPage === totalPages - 1"
                    class="page-link-custom prev-next-btn d-flex align-items-center justify-content-center"
                    aria-label="Trang sau"
                  >
                    <icon icon="fa-solid fa-chevron-right" />
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </section>
    </main>

    <Footer />
  </div>
</template>

<script src="./category-detail.js"></script>
<style scoped src="./category-detail.css"></style>
